import {
  Modal,
  ModalBody,
  Text,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Button,
  HStack,
  VStack,
  Divider,
  Box,
  List,
  ListItem,
  UnorderedList,
  ListIcon,
  Link,
  Icon,
  Spacer,
  IconButton,
  Checkbox,
} from "@chakra-ui/react";
import { CardContent, Card, CardHeading } from "../../components/chakra/card";
import { RiCheckLine, RiExternalLinkLine, RiLink } from "react-icons/ri";
import { useState } from "react";
import { api } from "../../utilities/apiRequest";
import { useAppState } from "../../components/App/AppProvider";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function UpgradeNowModal({ isOpen, onClose }: Props) {
  const upgradeMutation = api.license.upgrade.useMutation();
  const [isCompleting, setIsCompleting] = useState(false);

  const { app } = useAppState();

  const [step, setStep] = useState<"choose-plan" | "terms" | "complete">(
    "choose-plan",
  );
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const handleClickUpgradeStd = () => {
    setStep("terms");
  };

  const handleClose = () => {
    setStep("choose-plan");
    setIsTermsAccepted(false);
    onClose();
  };

  const handleClickCompleteUpgrade = async () => {
    if (!isTermsAccepted) {
      return;
    }
    await upgradeMutation.mutateAsync({ to: "lc_st3" });
    setStep("complete");
  };

  const handleClickContinueToCC = () => {
    // Wait 3s then refresh the page (this gives enough time for the cache to expire for the license upgrade)
    setIsCompleting(true);
    setTimeout(() => {
      setIsCompleting(false);
      window.location.href = "/";
    }
    , 3000);
  }

  return (
    <Modal
      closeOnEsc={step !== "complete"}
      closeOnOverlayClick={step !== "complete"}
      autoFocus={false}
      size={"2xl"}
      isOpen={isOpen}
      onClose={handleClose}>
      <ModalOverlay />
      <chakra-scope>
        <ModalContent>
          <ModalHeader>Upgrade plan</ModalHeader>
          {step !== "complete" && <ModalCloseButton />}
          <ModalBody>
            <VStack w="100%" spacing={6}>
              <Card
                w="100%"
                variant="solid"
                bg={step === "complete" ? "green.50" : undefined}>
                <CardHeading title="CertCrowd Standard">
                  <VStack align="start" spacing={0} fontSize="sm">
                    <HStack spacing={1}>
                      <Text fontSize="2xl" fontWeight="500">
                        $
                      </Text>
                      <Text fontSize="2xl" fontWeight="700">
                        1,000
                      </Text>
                      <Text pl="2" fontSize="sm" color="dimgray">
                        / year (ex. gst)
                      </Text>
                    </HStack>
                    <Text fontStyle="italic">For up to 3 users</Text>
                  </VStack>
                </CardHeading>
                <CardContent>
                  {step === "choose-plan" ? (
                    <VStack w="100%" align="start">
                      <Text maxW="36rem">
                        Upgrade to CertCrowd standard to access advanced
                        features and do even more with CertCrowd.
                      </Text>
                      <List>
                        <ListItem>
                          <ListIcon as={RiCheckLine} color="green.500" />
                          Multiple, customisable frameworks
                        </ListItem>
                        <ListItem>
                          <ListIcon as={RiCheckLine} color="green.500" />
                          Custom fields
                        </ListItem>
                        <ListItem>
                          <ListIcon as={RiCheckLine} color="green.500" />
                          Advanced registers to suit your business
                        </ListItem>
                        <ListItem>
                          <ListIcon as={RiCheckLine} color="green.500" />
                          Create more reports
                        </ListItem>
                        <ListItem>
                          <ListIcon as={RiCheckLine} color="green.500" />
                          Advanced Risk
                        </ListItem>
                        <ListItem>
                          <ListIcon as={RiCheckLine} color="green.500" />
                          Template management
                        </ListItem>
                        <ListItem>
                          <Link
                            color="blue.600"
                            isExternal
                            href="https://certcrowd.com/pricing">
                            And more
                            <Icon
                              as={RiExternalLinkLine}
                              boxSize="16px"
                              ml="1"
                            />
                          </Link>
                        </ListItem>
                      </List>
                      <Button
                        onClick={handleClickUpgradeStd}
                        alignSelf="end"
                        size="md">
                        Upgrade to Standard
                      </Button>
                    </VStack>
                  ) : step === "terms" ? (
                    <VStack w="100%" align="start" spacing="4">
                      <VStack w="100%" align="start">
                        <Text maxW="36rem">
                          Good choice - You are about to upgrade to a paid
                          Certcrowd plan. Upon continuing you will gain
                          immediate access to the additional features of
                          CertCrowd.
                        </Text>
                        <Text maxW="36rem">
                          You will receive an invoice for the above amount
                          shortly. Please ensure this invoice is paid promptly
                          to avoid any disruption to your access to Certcrowd.
                        </Text>
                      </VStack>
                      <VStack w="100%" align="start">
                        <Text fontWeight={700} maxW="36rem">
                          To continue with upgrading please read and accept the
                          CertCrowd terms and conditions and privacy policy.
                        </Text>
                        <Box
                          p="1"
                          borderRadius="md"
                          borderColor="brand.300"
                          borderWidth="1px"
                          borderStyle="solid"
                          pos="relative"
                          w="full">
                          <Box pos="absolute" top="1" right="2">
                            <IconButton
                              variant="ghost"
                              borderRadius="full"
                              as="a"
                              href="https://certcrowd.com/terms-and-privacy"
                              target="_blank"
                              aria-label="View terms and conditions in new tab"
                              icon={<RiExternalLinkLine />}
                            />
                          </Box>
                          <Box
                            as="iframe"
                            src="https://certcrowd.com/terms-and-privacy"
                            width="100%"
                            height="320px"
                          />
                        </Box>
                      </VStack>
                      <Checkbox
                        isChecked={isTermsAccepted}
                        onChange={() => setIsTermsAccepted(!isTermsAccepted)}>
                        I accept the terms and conditions and agree to upgrade
                        to a paid CertCrowd plan.
                      </Checkbox>
                      <Button
                        isLoading={upgradeMutation.isPending}
                        alignSelf="end"
                        isDisabled={!isTermsAccepted}
                        onClick={handleClickCompleteUpgrade}>
                        Complete upgrade
                      </Button>
                    </VStack>
                  ) : step === "complete" ? (
                    <VStack w="100%" align="start" spacing="6">
                      <VStack w="100%" align="start">
                        <Text fontSize="lg">Thanks for upgrading!</Text>
                        <Text fontSize="lg">
                          You now have access to even more of CertCrowd. An
                          invoice will be sent to you shortly.
                        </Text>
                        <Text fontSize="lg">
                          If you have any questions please contact us at{" "}
                          <Link
                            color="blue.600"
                            isExternal
                            href="mailto:help@certcrowd.com"
                            textDecoration="underline">
                            help@certcrowd.com
                          </Link>
                        </Text>
                      </VStack>
                      <Button isLoading={isCompleting} onClick={handleClickContinueToCC} alignSelf="end">Continue to CertCrowd</Button>
                    </VStack>
                  ) : null}
                </CardContent>
              </Card>
              {step === "choose-plan" && (
                <>
                  <HStack w="90%">
                    <Box h="full" flexGrow="1">
                      <Divider />
                    </Box>
                    <Box>
                      <Text>Need something more?</Text>
                    </Box>
                    <Box flexGrow="1">
                      <Divider />
                    </Box>
                  </HStack>
                  <VStack w="100%" align="start">
                    <Text maxW="90%">
                      Have a chat to us about your needs and we can help you
                      find the right plan and support for your business.
                    </Text>
                    <Link
                      color="blue.600"
                      isExternal
                      href="https://certcrowd.com/pricing">
                      See our full plan options
                      <Icon as={RiExternalLinkLine} boxSize="16px" ml="1" />
                    </Link>
                    <Link
                      color="blue.600"
                      isExternal
                      href="https://certcrowd.com/contact">
                      Contact us
                      <Icon as={RiExternalLinkLine} boxSize="16px" ml="1" />
                    </Link>
                  </VStack>
                </>
              )}

              <Spacer />
            </VStack>
          </ModalBody>
        </ModalContent>
      </chakra-scope>
    </Modal>
  );
}
